import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Card } from '@/components/ui/card';
import { Send, CheckCircle2, CheckSquare, ListTodo } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * Compact KPI strip for staff dashboards. Shows the last 7 days of activity
 * for the *currently authenticated staff member* (scoped server-side; we never
 * surface another staff member's stats).
 *
 * Data source: GET /api/admin/staff/activity (require_staff). Backed by
 * existing audit_log on quotes + the assigned_to field on tickets.
 */
const StaffActivityStrip = () => {
  const [stats, setStats] = useState(null);
  const [error, setError] = useState(false);

  useEffect(() => {
    const load = async () => {
      try {
        const res = await axios.get(`${BACKEND_URL}/api/admin/staff/activity`, {
          withCredentials: true,
        });
        setStats(res.data);
      } catch (e) {
        setError(true);
      }
    };
    load();
  }, []);

  if (error) return null;

  return (
    <div
      className="grid grid-cols-2 md:grid-cols-4 gap-3 sm:gap-4 mb-6 sm:mb-8"
      data-testid="staff-activity-strip"
    >
      <KpiTile
        label="Quotes Sent"
        sub="last 7 days"
        value={stats?.quotes_sent_7d}
        Icon={Send}
        accent="text-blue-400"
        testId="kpi-quotes-sent-7d"
      />
      <KpiTile
        label="Quotes Accepted"
        sub="last 7 days"
        value={stats?.quotes_accepted_7d}
        Icon={CheckCircle2}
        accent="text-primary"
        testId="kpi-quotes-accepted-7d"
      />
      <KpiTile
        label="Tickets Closed"
        sub="last 7 days"
        value={stats?.tickets_closed_7d}
        Icon={CheckSquare}
        accent="text-primary"
        testId="kpi-tickets-closed-7d"
      />
      <KpiTile
        label="Open Tickets"
        sub="assigned to you"
        value={stats?.tickets_assigned_open}
        Icon={ListTodo}
        accent="text-amber-500"
        testId="kpi-tickets-open"
      />
    </div>
  );
};

const KpiTile = ({ label, sub, value, Icon, accent, testId }) => (
  <Card className="p-4 sm:p-5 border-border" data-testid={testId}>
    <div className="flex items-start justify-between">
      <div>
        <p className="text-[10px] uppercase tracking-widest text-muted-foreground">{label}</p>
        <p className={`text-2xl sm:text-3xl font-black mt-2 ${accent}`}>
          {value === null || value === undefined ? '—' : value}
        </p>
        <p className="text-[10px] text-muted-foreground mt-1">{sub}</p>
      </div>
      <div className="w-9 h-9 rounded-lg bg-muted/30 flex items-center justify-center">
        <Icon className="w-4 h-4 text-muted-foreground" />
      </div>
    </div>
  </Card>
);

export default StaffActivityStrip;
